/**
 * posthogEvents.js
 * CODiii · PostHog Event Tracking Utility
 *
 * USAGE:
 *   import { phIdentify, phGroup, phRegister, phEvent } from '../utils/posthogEvents';
 *
 * All PostHog calls are centralised here so event names and property shapes
 * are defined in one place. Components just call the named functions.
 *
 * DATA MODEL NOTES (confirmed from DynamoDB exports June 2026):
 *
 *   Company table:
 *     company.id              → orgId for group analytics   (e.g. "comp_75efe2c5...")
 *     company.name            → firm_name                   (e.g. "ERA Architects")
 *     company.planKey         → tier                        ('starter' | 'studio' | 'firm')
 *     company.subscriptionStatus → subscription_status      ('active' | 'inactive')
 *     company.createdAt       → company sign-up date        (ISO string)
 *
 *   CompanyMember table:
 *     member.userId           → matches Cognito sub (auth.user.profile.sub)
 *     member.companyId        → FK to Company.id
 *     member.role             → 'ADMIN' | 'MEMBER'
 *     member.status           → 'ACTIVE' | 'REMOVED'
 *     member.email            → user email
 *
 *   JOIN PATH: to get a logged-in user's company data, query CompanyMember
 *   where userId === auth.user.profile.sub, then fetch Company by companyId.
 *   A user can't belong to multiple companies.
 *
 * REQUIRES: posthog-js already initialised via PostHogProvider or posthog.init()
 */

import posthog from 'posthog-js';
import { markMilestoneComplete } from './onboardingProgress';

// ─── Internal team filter ────────────────────────────────────────────────────
// Any user whose email is in this list gets is_internal: true in PostHog,
// so you can exclude your own sessions from every funnel with a single filter.
const INTERNAL_EMAILS = [
  // Paige
  'paige.reiche@gmail.com',
  'pr@oquli.ca',
  'pr@oquli.onmicrosoft.com',
  // Dennis
  'dd@oquli.ca',
  'megazordhaneck@gmail.com',
  // Shivani
  'shivani@oquli.onmicrosoft.com',
  'su@oquli.ca',
  'shivani101201@gmail.com',
  'uppeshivani2001@gmail.com',
  // OQULi system accounts
  'sandbox@oquli.ca',
];

const isInternalUser = (email) =>
  INTERNAL_EMAILS.some((e) => e.toLowerCase() === email?.toLowerCase());


// ─── 1. IDENTIFY ─────────────────────────────────────────────────────────────
/**
 * Call once after a successful login / on app mount when a session is active.
 * Replace the existing posthog.identify() call with this.
 *
 * HOW TO CALL:
 *   After login, you should already be fetching the CompanyMember record for
 *   the logged-in user (to know their role and companyId), then fetching the
 *   Company record. Pass those results here alongside the Cognito profile.
 *
 * @param {object} params
 * @param {string} params.userId           — auth.user.profile.sub  (Cognito sub)
 * @param {string} params.email            — auth.user.profile.email
 * @param {string} params.name             — auth.user.profile.name or given_name
 * @param {string} params.firmName         — company.name           (Company table)
 * @param {string} params.tier             — company.planKey        ('starter' | 'studio' | 'firm')
 * @param {string} params.role             — member.role            ('ADMIN' | 'MEMBER')
 * @param {string} params.subscriptionStatus — company.subscriptionStatus ('active' | 'inactive')
 */
export function phIdentify({
  userId,
  email,
  name,
  firmName,
  tier,
  role,
  subscriptionStatus,
}) {
  if (!userId) return;

  posthog.identify(userId, {
    email,
    $name: name, // PostHog's internal display-name property, used by Workflows to render "who" in message templates
    firm_name: firmName ?? null,
    tier: tier ?? null,                        // company.planKey: 'starter' | 'studio' | 'firm'
    role: role?.toLowerCase() ?? null,         // member.role lowercased: 'admin' | 'member'
    subscription_status: subscriptionStatus ?? null,
    is_internal: isInternalUser(email),
  });
}


// ─── 2. GROUP ────────────────────────────────────────────────────────────────
/**
 * Call immediately after phIdentify. Associates the user with their firm so
 * you can see "how many firms are active" rather than just individual users.
 *
 * ⚠️  Requires "Group analytics" to be enabled first:
 *     PostHog → Project Settings → Group Analytics → Enable (we have to pay for this if we do, so we will leave this off for now)
 *
 * @param {object} params
 * @param {string} params.orgId    — company.id  (e.g. "comp_75efe2c5...")
 * @param {string} params.firmName — company.name
 * @param {string} params.tier     — company.planKey
 */
export function phGroup({ orgId, firmName, tier }) {
  if (!orgId) return;

  posthog.group('firm', orgId, {
    name: firmName ?? null,
    tier: tier ?? null,
  });
}


// ─── 3. SUPER PROPERTIES ─────────────────────────────────────────────────────
/**
 * Call once after login. These auto-attach to every subsequent event in the
 * session so you can segment any funnel by tier without repeating it per-event.
 *
 * Also call phSetActiveProject() whenever the user switches projects.
 *
 * @param {string} params.tier            — company.planKey
 * @param {number} params.projectCount    — total projects for this company
 * @param {string} params.activeProjectId — currently selected project ID
 */
export function phRegister({ tier, projectCount, activeProjectId }) {
  posthog.register({
    tier: tier ?? null,
    project_count: projectCount ?? null,
    active_project_id: activeProjectId ?? null,
  });
}

/** Call when the user switches projects so active_project_id stays current. */
export function phSetActiveProject(projectId) {
  posthog.register({ active_project_id: projectId ?? null });
}


// ─── 4. DAILY ACTIVE USER ────────────────────────────────────────────────────
/**
 * Fire once per user per calendar day — your clean DAU signal.
 * Uses sessionStorage to gate the fire so it doesn't repeat on every page load.
 *
 * WHERE: Top-level app component (Dashboard.jsx or ProjectDashboard.jsx),
 *        inside the useEffect that runs after login resolves.
 *
 * @param {string} params.companyCreatedAt — company.createdAt (ISO string)
 *                 Used to compute days_since_signup at the company level, since
 *                 that's the most meaningful cohort unit for a B2B product.
 */
export function phDailyOpen({ companyCreatedAt } = {}) {
  const TODAY_KEY = 'ph_daily_open_date';
  const today = new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'
  if (sessionStorage.getItem(TODAY_KEY) === today) return;

  const daysSinceSignup = companyCreatedAt
    ? Math.floor((Date.now() - new Date(companyCreatedAt).getTime()) / 86400000)
    : null;

  posthog.capture('app_opened_daily', {
    days_since_signup: daysSinceSignup,
  });

  sessionStorage.setItem(TODAY_KEY, today);
}


// ─── 5. RESET ON SIGN OUT ────────────────────────────────────────────────────
/**
 * Call in the onSignOut handler (passed to TopNav) BEFORE clearing auth state.
 * Resets PostHog identity so the next login doesn't bleed into the prior session.
 */
export function phReset() {
  posthog.reset();
}


// ─── 6. ONBOARDING MILESTONE EVENTS ─────────────────────────────────────────

/** WHERE: ProjectWizard.jsx — after successful project creation API call. */
export function phProjectCreated({ projectId, projectName, isFirstProject }) {
  posthog.capture('project_created', {
    project_id: projectId,
    project_name: projectName,
    is_first_project: isFirstProject ?? false,
  });
}

/** WHERE: TopNav or Dashboard — onSelectProject handler. */
export function phProjectOpened({ projectId, projectName }) {
  posthog.capture('project_opened', {
    project_id: projectId,
    project_name: projectName,
  });
}

/**
 * WHERE: ProjectSettings.jsx — after successful construction type save.
 * @param {string} method — 'wizard' | 'manual' | 'bypass_research' | 'left_unset'
 */
export function phConstructionTypeSet({ projectId, method }) {
  posthog.capture('construction_type_set', {
    project_id: projectId,
    method,
  });
}

/**
 * WHERE: SharePointIntegration.jsx / GoogleDriveIntegration.jsx — after URL submitted.
 * @param {string} source — 'sharepoint' | 'gdrive'
 */
export function phFolderLinked({ projectId, source, isFirstFolder }) {
  posthog.capture('folder_linked', {
    project_id: projectId,
    source,
    is_first_folder: isFirstFolder ?? false,
  });
}

/** WHERE: Verification modal — on mount / when it becomes visible. */
export function phVerificationModalOpened({ projectId, docCount, unknownCount }) {
  posthog.capture('verification_modal_opened', {
    project_id: projectId,
    doc_count: docCount,
    unknown_count: unknownCount,
  });
}

/**
 * WHERE: Verification modal — Continue button handler, after API call succeeds.
 */
export function phExtractionApproved({ projectId, requirementDocCount }) {
  posthog.capture('extraction_approved', {
    project_id: projectId,
    requirement_doc_count: requirementDocCount,
    saved_as_draft: false,
  });
}

/** WHERE: Verification modal — Save as Draft button handler. */
export function phExtractionSavedAsDraft({ projectId }) {
  posthog.capture('extraction_approved', {
    project_id: projectId,
    saved_as_draft: true,
  });
}

/**
 * WHERE: Verification modal — document type override dropdown onChange.
 * @param {string} fromType — classifier-assigned type e.g. 'requirement'
 * @param {string} toType   — user override e.g. 'deliverable'
 */
export function phDocumentReclassified({ projectId, fromType, toType }) {
  posthog.capture('unknown_doc_reclassified', {
    project_id: projectId,
    from_type: fromType,
    to_type: toType,
  });
}

/**
 * 🚀 TRUE ACTIVATION MOMENT — Requirements Register first populated.
 *
 * PREFERRED: Fire server-side from the Extraction Lambda (Step 3) so it's
 * captured even if the user closes the tab during processing. See the
 * Implementation Guide for the Python snippet.
 *
 * Client-side fallback: fire when extraction completion is confirmed in the UI.
 * @param {number} [durationMs] — elapsed time since extraction was kicked off
 *   (from the sessionStorage timestamp stamped at phExtractionApproved), when
 *   available in this session; null after a page reload since that timestamp
 *   doesn't persist.
 */
export function phRegisterPopulated({ projectId, requirementCount, durationMs }) {
  posthog.capture('register_populated', {
    project_id: projectId,
    requirement_count: requirementCount,
    duration_ms: durationMs ?? null,
  });
}

/**
 * WHERE: ExtractedRequirementsPage.jsx — fetchRequirements, when called with
 * a searchStartedAt timestamp (i.e. from a real user search, not a tab/filter
 * change). Fires once results for that search return, whether zero or many.
 * @param {number} queryLength — length of the search string (not the text
 *   itself — avoids logging free-text user input).
 * @param {number} durationMs — from the debounced search firing to results
 *   rendering, i.e. actual query latency.
 */
export function phRequirementSearched({ projectId, tab, queryLength, resultCount, durationMs }) {
  posthog.capture('requirement_searched', {
    project_id: projectId,
    tab: tab ?? null,
    query_length: queryLength ?? 0,
    result_count: resultCount ?? 0,
    duration_ms: durationMs ?? null,
  });
}

/**
 * WHERE: TeamOverviewDialog.jsx or UsersDialog.jsx — after successful invite API call.
 */
export function phTeamMemberInvited({ projectId, inviteeCount }) {
  posthog.capture('team_member_invited', {
    project_id: projectId,
    invitee_count: inviteeCount ?? 1,
  });
}

/** WHERE: SlackCallback.jsx — on successful OAuth callback. */
export function phSlackConnected() {
  posthog.capture('slack_connected');
}


// ─── 7. SCAN FUNNEL EVENTS ───────────────────────────────────────────────────

/** WHERE: ComplianceScanModal.jsx — on mount. */
export function phScanModalOpened({ projectId }) {
  posthog.capture('scan_modal_opened', { project_id: projectId });
}

/**
 * WHERE: ComplianceScanModal.jsx — scan path radio/select onChange.
 * @param {number|string} scanPath — 1 | 2 | 3
 */
export function phScanPathSelected({ projectId, scanPath }) {
  posthog.capture('scan_path_selected', {
    project_id: projectId,
    scan_path: scanPath,
  });
}

/**
 * WHERE: ComplianceScanModal.jsx — Run Scan button handler (before API call).
 * @param {string} complianceType — 'BUILDING_CODE' | 'CLIENT_REQUIREMENTS' | 'ZONING' | 'STANDARDS'
 */
export function phScanRun({ projectId, scanPath, complianceType, deliverableCount }) {
  posthog.capture('scan_run', {
    project_id: projectId,
    scan_path: scanPath,
    compliance_type: complianceType,
    deliverable_count: deliverableCount,
  });
}

/**
 * PREFERRED: Fire server-side from scan Lambda. See Implementation Guide.
 * Client-side fallback: fire when scan results load in ComplianceDashboard.jsx.
 */
export function phScanCompleted({ projectId, scanPath, complianceType, issueCount, durationSeconds }) {
  posthog.capture('scan_completed', {
    project_id: projectId,
    scan_path: scanPath,
    compliance_type: complianceType,
    issue_count: issueCount,
    duration_seconds: durationSeconds ?? null,
    zero_results: issueCount === 0,
  });
}

/**
 * WHERE: Error handler in ComplianceDashboard.jsx's scan poller.
 * @param {string} reason — brief error category, no PII
 * @param {string} [errorMessage] — human-readable detail, when available
 *
 * NOTE: ComplianceChecklistWorker's `_set_scan_status(...)` (backend) never
 * writes a reason/message on failure today — every soft-failure call site
 * (fewer than 2 compliance types, no OpenAI client, missing requirements,
 * etc.) just sets status="failed" with no detail, so there is currently no
 * rich message available to pass here from the frontend poller. Backfilling
 * that would need a backend change to ComplianceChecklistWorker, which has
 * unpublished, actively-changing work in flight (see DEPLOY_CHECKLIST.md) —
 * flagging as a follow-up rather than touching that file speculatively.
 */
export function phScanFailed({ projectId, scanPath, reason, errorMessage }) {
  posthog.capture('scan_failed', {
    project_id: projectId,
    scan_path: scanPath,
    reason: reason ?? 'unknown',
    error_message: errorMessage ? String(errorMessage).slice(0, 200) : null,
  });
}

/**
 * One event per compliance finding, fired when scan results render in
 * ComplianceDashboard.jsx (alongside/after phScanCompleted's aggregate
 * issue_count). This is what makes "what verdicts came back" queryable
 * per-finding rather than only as a scan-level total.
 *
 * findingVerdict is exclusively for predicate/rule-based compliance
 * findings — it must never be inferred from chatbot behaviour (see
 * phChatbotFindingGenerated's responseOutcome for that, a deliberately
 * separate taxonomy).
 * WHERE: ComplianceDashboard.jsx — wherever raw scan issues are normalized
 * (normalizeAndEnrichIssue) into rendered findings.
 * @param {string} findingVerdict — 'compliant' | 'non_compliant' | 'needs_review' |
 *   'not_applicable' | 'uncertain' | 'information_not_available'
 */
export function phFindingGenerated({ projectId, scanId, issueId, findingVerdict, topic, requirementId, hasCitation }) {
  posthog.capture('finding_generated', {
    project_id: projectId,
    scan_id: scanId ?? null,
    issue_id: issueId ?? null,
    finding_verdict: findingVerdict ?? null,
    topic: topic ?? null,
    requirement_id: requirementId ?? null,
    has_citation: !!hasCitation,
  });
}


// ─── 8. ISSUE / COMPLIANCE TRACKER EVENTS ────────────────────────────────────

/**
 * First time a user views an issue detail panel this session.
 * WHERE: IssueCard.jsx or detail panel — on panel open.
 * Fires only once per session (gated by sessionStorage).
 */
export function phFirstIssueViewed({ projectId }) {
  const KEY = 'ph_first_issue_viewed';
  if (sessionStorage.getItem(KEY)) return;
  posthog.capture('first_issue_viewed', { project_id: projectId });
  sessionStorage.setItem(KEY, '1');
}

/**
 * WHERE: Issue detail panel — status dropdown / resolve button handler.
 *
 * @param {string} fromStatus    — previous status e.g. 'open'
 * @param {string} toStatus      — new status e.g. 'resolved'
 * @param {string} overrideType  — from override_type dropdown: 'not_applicable_to_project' |
 *                                 'requirement_satisfied' | 'duplicate_finding' | 'other' | null
 */
export function phIssueStatusChanged({ projectId, issueId, fromStatus, toStatus, overrideType }) {
  posthog.capture('issue_status_changed', {
    project_id: projectId,
    issue_id: issueId,
    from_status: fromStatus,
    to_status: toStatus,
    override_type: overrideType ?? null,
  });
}

/** WHERE: Issue detail panel — assignee dropdown onChange. */
export function phIssueAssigned({ projectId, issueId }) {
  posthog.capture('issue_assigned', { project_id: projectId, issue_id: issueId });
}

/**
 * WHERE: Issue detail panel — status dropdown handler, when toStatus === 'dismissed'.
 * Dedicated event (separate from issue_status_changed) so dismissal volume can be
 * tracked directly, since the override reason isn't picked until the follow-up
 * phIssueDismissalReasonSelected event.
 */
export function phIssueDismissed({ projectId, issueId }) {
  posthog.capture('issue_dismissed', { project_id: projectId, issue_id: issueId });
}

/**
 * WHERE: Issue detail panel — Zone 3 override reason dropdown onChange.
 * Each reason is a direct scan-accuracy signal: 'requirement_satisfied' means the
 * scan missed evidence that was actually present, i.e. a false positive.
 * @param {string} overrideType — 'not_applicable_to_project' | 'requirement_satisfied' |
 *                                 'duplicate_finding' | 'other'
 */
export function phIssueDismissalReasonSelected({ projectId, issueId, overrideType }) {
  posthog.capture('issue_dismissal_reason_selected', {
    project_id: projectId,
    issue_id: issueId,
    override_type: overrideType,
    indicates_scan_inaccuracy: overrideType === 'requirement_satisfied',
  });
}

/** WHERE: Evidence upload handler (Scenario B — scan missed evidence). */
export function phEvidenceUploaded({ projectId, issueId }) {
  posthog.capture('issue_evidence_uploaded', { project_id: projectId, issue_id: issueId });
}

/**
 * First issue resolved this session.
 * WHERE: Call from inside phIssueStatusChanged when toStatus === 'resolved'.
 * Fires only once per session (gated by sessionStorage).
 */
export function phFirstIssueResolved({ projectId }) {
  const KEY = 'ph_first_issue_resolved';
  if (sessionStorage.getItem(KEY)) return;
  posthog.capture('first_issue_resolved', { project_id: projectId });
  sessionStorage.setItem(KEY, '1');
}


// ─── 9. RESEARCH (CHATBOT) EVENTS ────────────────────────────────────────────

/**
 * WHERE: DashboardChatbot.jsx — after the message is sent (not on keypress).
 * @param {number} docCountInScope — activeDocuments?.length at time of query
 */
export function phResearchQuerySent({ projectId, docCountInScope }) {
  posthog.capture('research_query_sent', {
    project_id: projectId,
    doc_count_in_scope: docCountInScope ?? null,
  });
}

/**
 * WHERE: DashboardChatbot.jsx — handleDocumentToggle, on every check/uncheck
 * of a document in the Selected/Available Files panels or the doc-selection
 * modal. Behaviour, not judgment: fires on the raw toggle, not just on the
 * batch "Analyze" confirm.
 * @param {string} action — 'select' | 'deselect'
 */
export function phDocumentSelected({ projectId, docId, docType, action, docCountInScopeAfter }) {
  posthog.capture('document_selected', {
    project_id: projectId,
    doc_id: docId,
    doc_type: docType ?? null,
    action,
    doc_count_in_scope_after: docCountInScopeAfter ?? null,
  });
}

/**
 * WHERE: DashboardChatbot.jsx — startPolling, once a job's status flips to
 * 'completed' (not on failure/timeout — see phChatbotResponseFailed for that,
 * which is the correct home for genuine technical failures/timeouts — those
 * don't belong on this event).
 * @param {number} durationMs — Date.now() at completion minus the timestamp
 *   captured when research_query_sent fired for this message.
 * @param {number} citationCount — references.length on the returned message.
 *   Descriptive only — NOT used as an uncertainty/quality signal (see
 *   phChatbotFindingGenerated for that).
 */
export function phChatbotResponse({ projectId, durationMs, citationCount }) {
  posthog.capture('chatbot_response', {
    project_id: projectId,
    duration_ms: durationMs ?? null,
    citation_count: citationCount ?? 0,
  });
}

/**
 * The chatbot's counterpart to a compliance-scan finding — "what did this
 * answer actually turn out to be," not just "a response came back."
 * WHERE: DashboardChatbot.jsx — same call site as phChatbotResponse, reading
 * the `response_outcome` field requirementsChatbotChat now writes to the job
 * row directly (either from the "no results" branch, which completes the job
 * instead of timing out, or from the answer-generation LLM call's own
 * structured `outcome` field).
 *
 * Deliberately NOT citation-count-based: zero citations is consistent with
 * several very different situations (nothing relevant found, an answer that
 * didn't need document evidence, citation extraction failing, an ungrounded
 * answer, an unclear question, or a timeout) — collapsing all of those into
 * one boolean was the previous, unreliable heuristic. This field is the
 * backend's own explicit classification instead.
 *
 * Also deliberately NOT reusing 'uncertain'/'information_not_available' —
 * those are structured compliance verdicts reserved for predicate evaluation
 * (see phFindingGenerated's findingVerdict) and should never be inferred from
 * chatbot behaviour.
 * @param {string} responseOutcome — 'answered' | 'not_found_in_selected_documents' |
 *   'more_information_needed' | 'conflicting_information'. (A 5th conceptual
 *   category, technical_failure, is intentionally not emitted here — that's
 *   phChatbotResponseFailed's job, on the job-failed/timeout path, which by
 *   design still doesn't complete.)
 */
export function phChatbotFindingGenerated({ projectId, responseOutcome, citationCount, docCountInScope }) {
  posthog.capture('chatbot_finding_generated', {
    project_id: projectId,
    response_outcome: responseOutcome,
    citation_count: citationCount ?? 0,
    doc_count_in_scope: docCountInScope ?? null,
  });
}

/**
 * WHERE: DashboardChatbot.jsx (ReferencePill onClick) and
 * ExtractedRequirementsPage.jsx (handleOpenSourceDoc / DocPill onClick) —
 * any click that opens a source-document citation. The "deeper research"
 * signal: a user chasing a citation back to its source, not just glancing
 * at a snippet.
 * @param {string} source — 'chatbot' | 'register'
 */
export function phCitationClicked({ projectId, source, docId, pageNumber, requirementId }) {
  posthog.capture('citation_clicked', {
    project_id: projectId,
    source,
    doc_id: docId ?? null,
    page_number: pageNumber ?? null,
    requirement_id: requirementId ?? null,
  });
}

/**
 * WHERE: DashboardChatbot.jsx — inside persistThumb, after existing state update.
 * @param {string} value — 'up' | 'down'
 */
export function phResearchThumb({ projectId, messageIndex, value }) {
  posthog.capture('research_thumb', {
    project_id: projectId,
    message_index: messageIndex,
    value,
  });
}


// ─── 10. ONBOARDING TOUR EVENTS (Shepherd.js) ────────────────────────────────
// Wire into Shepherd tour lifecycle events in AppTour.jsx.

export function phTourStarted({ tourId }) {
  posthog.capture('tour_started', { tour_id: tourId });
  posthog.setPersonProperties({ tour_started: true });
}

/** @param {string|number} stepId — the Shepherd step's id property */
export function phTourStepCompleted({ tourId, stepId }) {
  posthog.capture('tour_step_completed', { tour_id: tourId, step_id: stepId });
  posthog.setPersonProperties({ tour_step_completed: true });
}

export function phTourSkipped({ tourId, stepId }) {
  posthog.capture('tour_skipped', { tour_id: tourId, last_step_seen: stepId });
  posthog.setPersonProperties({ tour_skipped: true });
}

export function phTourCompleted({ tourId }) {
  posthog.capture('tour_completed', { tour_id: tourId });
  posthog.setPersonProperties({ tour_completed: true });
}

// ─── Tour milestone events (decoupled from the Shepherd tour UI) ─────────────
// Fired directly from the real action handlers, so they reflect what a user
// actually did whether or not they're engaging with the AppTour overlay.
// OnboardingChecklist.jsx listens for these exact event names.

// Each also persists the milestone to localStorage (markMilestoneComplete) so
// the AppTour can resume from the right place and the OnboardingChecklist can
// reflect real progress on refresh — until the PostHog Query API Lambda proxy
// (which reads the person properties set below back across devices) is built.

export function phTourFolderLinked() {
  posthog.capture('tour_folder_linked');
  posthog.setPersonProperties({ tour_folder_linked: true });
  markMilestoneComplete('tour_folder_linked');
}

export function phTourResearchUsed() {
  posthog.capture('tour_research_used');
  posthog.setPersonProperties({ tour_research_used: true });
  markMilestoneComplete('tour_research_used');
}

export function phTourRegisterViewed() {
  posthog.capture('tour_register_viewed');
  posthog.setPersonProperties({ tour_register_viewed: true });
  markMilestoneComplete('tour_register_viewed');
}

export function phTourScanInitiated() {
  posthog.capture('tour_scan_initiated');
  posthog.setPersonProperties({ tour_scan_initiated: true });
  markMilestoneComplete('tour_scan_initiated');
}

export function phTourSettingsOpened() {
  posthog.capture('tour_settings_opened');
  posthog.setPersonProperties({ tour_settings_opened: true });
  markMilestoneComplete('tour_settings_opened');
}


// ─── 11. UPGRADE / EXPANSION SIGNALS ─────────────────────────────────────────
// Commercially the most important events — each is a warm upgrade lead.

/**
 * Starter user saw the locked Compliance Tracker upsell teaser.
 * WHERE: Paywall.jsx or wherever the locked tracker state renders for starter users.
 */
export function phTrackerUpsellViewed({ projectId }) {
  posthog.capture('tracker_upsell_viewed', { project_id: projectId });
}

/** WHERE: Same as above — CTA / upgrade button onClick. */
export function phTrackerUpsellClicked({ projectId }) {
  posthog.capture('tracker_upsell_clicked', { project_id: projectId });
}

/**
 * Starter user tried to run a scan and hit the tier wall.
 * This is your single most important expansion-intent signal.
 * WHERE: Wherever scan access is gated for starter plan users.
 */
export function phScanBlockedByTier({ projectId }) {
  posthog.capture('scan_blocked_by_tier', { project_id: projectId });
}


// ─── 12. FOLDER LINK ERRORS ──────────────────────────────────────────────────
/**
 * Silent activation killer — captures failed folder links.
 * WHERE: SharePointIntegration.jsx / GoogleDriveIntegration.jsx error handlers.
 * @param {string} reason — 'bad_url' | 'auth_error' | 'unsupported_type' | 'site_not_granted' | 'provider_mismatch' | 'unknown'
 * @param {string} [errorMessage] — the actual user-facing message shown, when available; truncated defensively
 */
export function phFolderLinkFailed({ projectId, source, reason, errorMessage }) {
  posthog.capture('folder_link_failed', {
    project_id: projectId,
    source,
    reason,
    error_message: errorMessage ? String(errorMessage).slice(0, 200) : null,
  });
}


// ─── 13. ERROR / EXCEPTION CAPTURE ───────────────────────────────────────────
/**
 * Reports a caught error to PostHog ($exception event) and stamps the
 * person's most recent error so it's visible on their Person profile without
 * waiting for a support ticket.
 * @param {unknown} error
 * @param {object} [context] — non-sensitive metadata only, e.g. { source: 'graphql_request' }
 */
export function phErrorCaught(error, context = {}) {
  posthog.captureException(error, context);
  posthog.setPersonProperties({
    last_error_message: error?.message ?? String(error),
    last_error_source: context.source ?? null,
    last_error_at: new Date().toISOString(),
  });
}


// ─── 14. PIPELINE FAILURE EVENTS ─────────────────────────────────────────────
/**
 * Stamps a Person profile with the most recent pipeline failure so PostHog's
 * People view can filter "who hit a failure in the last N days" without
 * digging through raw events — this is what makes outreach (e.g. emailing a
 * user whose folder link keeps failing) tractable instead of relying on
 * someone noticing a Slack alert for a specific person.
 */
function stampPipelineFailure(stage, reason) {
  posthog.setPersonProperties({
    last_pipeline_failure_stage: stage,
    last_pipeline_failure_reason: reason ?? 'unknown',
    last_pipeline_failure_at: new Date().toISOString(),
  });
}

/**
 * WHERE: ClassifierModal.jsx — applyPollResults (whole-job upload failure,
 * scope: 'upload') and the per-file FAILED-row detection (scope: 'file').
 * @param {string} scope — 'upload' | 'file'
 * @param {string} reason — error code, e.g. 'SHAREPOINT_SITE_NOT_GRANTED' | 'UPLOAD_FAILED'
 * @param {string} [errorMessage] — short, non-sensitive description; truncated defensively
 */
export function phDocumentClassificationFailed({ projectId, jobId, scope, reason, errorMessage }) {
  posthog.capture('document_classification_failed', {
    project_id: projectId,
    job_id: jobId,
    scope,
    reason: reason ?? 'unknown',
    error_message: errorMessage ? String(errorMessage).slice(0, 200) : null,
  });
  stampPipelineFailure('classification', reason);
}

/**
 * WHERE: NotificationsContext.js — resumePollingForJob's onError branch,
 * after the err?.code === 'FAILED' check (a confirmed backend failure, not a
 * transient network/poll blip).
 * @param {string} [errorMessage] — the CHUNKING_JOBS_TABLE row's `message` field
 *   (the reason string extractTextWorker/documentChunking wrote on failure)
 */
export function phDocumentIndexingFailed({ projectId, jobId, reason, errorMessage }) {
  posthog.capture('document_indexing_failed', {
    project_id: projectId,
    job_id: jobId,
    reason: reason ?? 'unknown',
    error_message: errorMessage ? String(errorMessage).slice(0, 200) : null,
  });
  stampPipelineFailure('indexing', reason);
}

/**
 * WHERE: NotificationsContext.js — resumePollingForJob's onDone branch.
 * @param {number} [durationMs] — Date.now() minus the startedAt timestamp
 *   stamped in jobMeta when the job was originally kicked off in
 *   RequirementsPage.jsx. Only available when indexing started this session
 *   (jobMeta doesn't survive a page reload) — null otherwise rather than a
 *   misleading number from a resumed poll.
 */
export function phDocumentIndexed({ projectId, jobId, durationMs }) {
  posthog.capture('document_indexed', {
    project_id: projectId,
    job_id: jobId,
    duration_ms: durationMs ?? null,
  });
}

/**
 * WHERE: NotificationsContext.js — notification ingest, when a fetched
 * notification has type === 'EXTRACTION_FAILED' (written server-side by
 * requirementsExtractionWorker's stale-extraction check).
 * @param {string} [errorMessage] — the notification's own `message` text
 *   (built server-side with the doc name + stale threshold)
 */
export function phRequirementExtractionFailed({ projectId, jobId, reason, errorMessage }) {
  posthog.capture('requirement_extraction_failed', {
    project_id: projectId,
    job_id: jobId,
    reason: reason ?? 'stale_extraction',
    error_message: errorMessage ? String(errorMessage).slice(0, 200) : null,
  });
  stampPipelineFailure('requirement_extraction', reason);
}

/**
 * WHERE: DashboardChatbot.jsx — markMessageFailed, when a polled job comes
 * back with status: 'failed', times out, or the send itself throws.
 * @param {string} [reason] — short failure category: 'job_failed' | 'timeout' | 'exception'
 * @param {string} [errorMessage] — the actual message shown in the failed chat bubble
 */
export function phChatbotResponseFailed({ projectId, reason, errorMessage }) {
  posthog.capture('chatbot_response_failed', {
    project_id: projectId,
    reason: reason ?? 'unknown',
    error_message: errorMessage ? String(errorMessage).slice(0, 200) : null,
  });
  stampPipelineFailure('chatbot', reason);
}
